import { CheckCircledIcon } from "@radix-ui/react-icons"

import ActionButton from "./ActionButton/ActionButton"
import {
	canEdit,
	claimingConversionExplainMessage,
	formatMenusAndCoupons,
	getRereservationOriginalPrice,
	getSpanClass,
	getStatusDisplayText,
} from "./CancelsForRereservationTableView.logic"
import {
	isClaimingConversion,
	renderVisitCount,
} from "./CancelsTableView.common.logic"
import styles from "./MobileCancelsForRereservationTableView.module.scss"

import QuestionExplanationIcon from "@/lib/components/ui/QuetionExplanationIcon/QuestionExplanationIcon"
import { CancelForRereservationFragment } from "@/main/gql/__generated__/graphql"
import { useCancelTypeInfos } from "@/main/lib/useCancelTypeInfos"
import {
	convertGraphQLDateToDate,
	formatJstDateUntilDayByHyphen,
} from "@/utils/date"

import { EditingRereservationRow } from "../HomePage.logic"

type Props = {
	currentTab:
		| "ENCOURAGING_RERESERVATION_NOT_SCHEDULED"
		| "ENCOURAGING_RERESERVATION_SCHEDULED"
		| "RERESERVED"
	cancels: readonly CancelForRereservationFragment[]
	editingRereservationRowMap: Record<string, EditingRereservationRow> // key is idv2
	updateEditingRereservationRow: (
		idv2: string,
		row: EditingRereservationRow
	) => void
	onClickUpdateButton: (idv2: string) => void
	onClickActionButton: (idv2: string) => void
	onClickRereservationRowEdit: (idv2: string) => void
	cancelRereservationRowEdit: (idv2: string) => void
}

// 再予約金額セル: design 参照 frontend/design/store_management/rereservation_rereserved_design.png
// discountPrice あり時は元金額を主役表示し、下に RefundHub割引バッジと割引後金額を補足する.
type MobileRereservationPriceContentProps = {
	rereservation: {
		price: number
		discountPrice: number | null
	} | null
}

const MobileRereservationPriceContent = ({
	rereservation,
}: MobileRereservationPriceContentProps) => {
	if (!rereservation) {
		return null
	}
	const discountedPrice = rereservation.price
	const discount = rereservation.discountPrice
	if (discount == null || discount === 0) {
		return <span>¥{discountedPrice}</span>
	}
	const originPrice = getRereservationOriginalPrice(rereservation)
	return (
		<div className={styles.mob_price_section}>
			<span className={styles.mob_price_main}>¥{originPrice}</span>
			<div className={styles.mob_discount_row}>
				<span className={styles.mob_discount_badge}>
					<span className={styles.mob_discount_badge_text}>RefundHub割引</span>
				</span>
				<span className={styles.mob_discount_text}>
					-¥{discount}（割引後 ¥{discountedPrice}）
				</span>
			</div>
		</div>
	)
}

export const MobileCancelsForRereservationView = ({
	currentTab,
	cancels,
	editingRereservationRowMap,
	updateEditingRereservationRow,
	onClickUpdateButton,
	onClickActionButton,
	onClickRereservationRowEdit,
	cancelRereservationRowEdit,
}: Props) => {
	const { getCancelTypeLabel } = useCancelTypeInfos()
	const isEditingRow = (idv2: string): boolean => {
		return idv2 in editingRereservationRowMap
	}

	// TODO: 全てCancelsTableViewと同じロジックなので複雑なやつはそれぞれcomponent化するべき
	return (
		<div className={styles.mobile_cancels_table_view}>
			<div className={styles.header}>
				<div className={styles.cancels_count}>{cancels.length}件の結果</div>
			</div>

			{cancels.map((row, _) => (
				<div className={styles.item} key={row.idv2}>
					<div className={styles.attribute}>
						<div className={styles.label}>ステータス</div>
						<div className={styles.content}>
							<span
								className={`${getSpanClass(row, styles)} ${
									styles.table_elm_background
								}`}
							>
								{getStatusDisplayText(row)}
							</span>
						</div>
					</div>

					<div className={styles.attribute}>
						<div className={styles.label}>
							請求転換
							<QuestionExplanationIcon
								contentMessage={claimingConversionExplainMessage}
							/>
						</div>
						<div className={styles.content}>
							{isClaimingConversion(row) && (
								<CheckCircledIcon className={styles.icon} />
							)}
						</div>
					</div>

					<div className={styles.attribute}>
						<div className={styles.label}>キャンセル種別</div>
						<div className={styles.content}>
							{getCancelTypeLabel(row.cancelType)}
						</div>
					</div>

					<div className={styles.attribute}>
						<div className={styles.label}>キャンセル処理日</div>
						<div className={styles.content}>{row.dateCancel ?? "不明"}</div>
					</div>

					<div className={styles.attribute}>
						<div className={styles.label}>来店回数</div>
						<div className={styles.content}>{renderVisitCount(row)}</div>
					</div>

					{currentTab === "RERESERVED" && (
						<>
							<div className={styles.attribute}>
								<div className={styles.label}>再予約ID</div>
								<div className={styles.content}>
									{row.rereservation?.scrapingTargetSiteReserveId}
								</div>
							</div>

							<div className={styles.attribute}>
								<div className={styles.label}>再予約金額</div>
								<div className={styles.content}>
									<MobileRereservationPriceContent
										rereservation={row.rereservation}
									/>
								</div>
							</div>

							<div className={styles.attribute}>
								<div className={styles.label}>再来店予定日</div>
								<div className={styles.content}>
									{row.rereservation?.startTime
										? formatJstDateUntilDayByHyphen(
												convertGraphQLDateToDate(row.rereservation.startTime)
											)
										: ""}
								</div>
							</div>

							<div className={styles.attribute}>
								<div className={styles.label}>担当スタッフ</div>
								<div className={styles.content}>
									{row.rereservation?.staffName ?? ""}
								</div>
							</div>

							<div className={styles.attribute}>
								<div className={styles.label}>施術時間</div>
								<div className={styles.content}>
									{row.rereservation?.durationMinute
										? `${row.rereservation.durationMinute}分`
										: ""}
								</div>
							</div>

							<div className={styles.attribute}>
								<div className={styles.label}>施術メニュー</div>
								<div className={styles.content}>
									{row.rereservation ? formatMenusAndCoupons(row) : ""}
								</div>
							</div>
						</>
					)}

					<div className={styles.attribute}>
						<div className={styles.label}>お客様</div>
						<div className={styles.content}>
							<span>{row.name}</span>
						</div>
					</div>

					<div className={styles.attribute}>
						<div className={styles.label}>電話番号</div>
						<div className={styles.content}>
							{isEditingRow(row.idv2) ? (
								<input
									className={styles.editing_cell}
									type="number"
									value={editingRereservationRowMap[row.idv2].phoneNumber}
									onChange={(event) => {
										const newRow: EditingRereservationRow = {
											...editingRereservationRowMap[row.idv2],
											phoneNumber: event.target.value,
										}
										updateEditingRereservationRow(row.idv2, newRow)
									}}
								/>
							) : (
								<span>{row.phoneNumber}</span>
							)}
						</div>
					</div>

					<div className={styles.footer}>
						{currentTab === "ENCOURAGING_RERESERVATION_NOT_SCHEDULED" &&
							!isClaimingConversion(row) && (
								<div className={styles.checkbox_edit_container}>
									{canEdit(row.rereservationStatus) && (
										<div className={styles.edit_button}>
											{isEditingRow(row.idv2) ? (
												<a
													href="#"
													onClick={(e) => {
														e.preventDefault()
														cancelRereservationRowEdit(row.idv2)
													}}
												>
													編集取消
												</a>
											) : (
												<a
													href="#"
													onClick={(e) => {
														e.preventDefault()
														onClickRereservationRowEdit(row.idv2)
													}}
												>
													編集
												</a>
											)}
										</div>
									)}
								</div>
							)}

						{(currentTab === "ENCOURAGING_RERESERVATION_NOT_SCHEDULED" ||
							currentTab === "ENCOURAGING_RERESERVATION_SCHEDULED") && (
							<div className={styles.action_button}>
								<ActionButton
									rereservationRow={row}
									isEditingRow={isEditingRow(row.idv2)}
									onClickUpdateButton={() => onClickUpdateButton(row.idv2)}
									onClickActionButton={() => onClickActionButton(row.idv2)}
									currentTab={currentTab}
								/>
							</div>
						)}
					</div>
				</div>
			))}
		</div>
	)
}

export default MobileCancelsForRereservationView
